/**
 * Feature 3D — Knowledge Hub QA Context pack (bounded compose).
 *
 * Deterministic summaries + deep links. Not authoring compile.
 * Not RAG. Recommendations are not truth.
 */
import { z } from 'zod';
import { EnvironmentDimensionEvidenceSchema, EnvironmentEvidenceBandSchema } from './environment-intelligence';

export const QA_CONTEXT_SECTION_STATUS = ['available', 'limited', 'unavailable'] as const;
export type QaContextSectionStatus = (typeof QA_CONTEXT_SECTION_STATUS)[number];

export const QA_CONTEXT_EVIDENCE_KIND = ['direct', 'strong', 'possible', 'limited', 'unavailable'] as const;
export type QaContextEvidenceKind = (typeof QA_CONTEXT_EVIDENCE_KIND)[number];

export const QaContextLinkSchema = z.object({
  label: z.string().max(80),
  href: z.string().max(500),
});
export type QaContextLink = z.infer<typeof QaContextLinkSchema>;

export const QaContextItemSchema = z.object({
  id: z.string().max(80).optional(),
  title: z.string().max(300),
  detail: z.string().max(400).optional(),
  href: z.string().max(500).optional(),
});
export type QaContextItem = z.infer<typeof QaContextItemSchema>;

export const QaContextSectionBaseSchema = z.object({
  status: z.enum(QA_CONTEXT_SECTION_STATUS),
  summary: z.string().max(500),
  why: z.array(z.string().max(300)).max(8).default([]),
  links: z.array(QaContextLinkSchema).max(8).default([]),
});

export const KnowledgeQaProjectContextSchema = QaContextSectionBaseSchema.extend({
  glossaryTermCount: z.number().int().nonnegative(),
  capabilityCount: z.number().int().nonnegative(),
  journeyCount: z.number().int().nonnegative(),
  requirementCount: z.number().int().nonnegative(),
  storyCount: z.number().int().nonnegative(),
  scenarioCount: z.number().int().nonnegative(),
  automationFlowCount: z.number().int().nonnegative(),
  authoringTokenCount: z.number().int().nonnegative().optional(),
  items: z.array(QaContextItemSchema).max(12).default([]),
});
export type KnowledgeQaProjectContext = z.infer<typeof KnowledgeQaProjectContextSchema>;

export const KnowledgeQaChangeContextSchema = QaContextSectionBaseSchema.extend({
  evidenceKind: z.enum(QA_CONTEXT_EVIDENCE_KIND),
  commitSha: z.string().max(64).nullable(),
  commitMessage: z.string().max(300).nullable(),
  fileCount: z.number().int().nonnegative(),
  impactedRequirementCount: z.number().int().nonnegative(),
  impactedScenarioCount: z.number().int().nonnegative(),
  impactedFlowCount: z.number().int().nonnegative(),
  contractDriftSupporting: z.boolean().default(false),
  tiaAnalysisStatus: z.string().max(40).nullable().optional(),
  changedFiles: z.array(z.string().max(300)).max(12).default([]),
  items: z.array(QaContextItemSchema).max(12).default([]),
});
export type KnowledgeQaChangeContext = z.infer<typeof KnowledgeQaChangeContextSchema>;

export const KnowledgeQaHistoricalContextSchema = QaContextSectionBaseSchema.extend({
  relatedFailureCount: z.number().int().nonnegative(),
  openCount: z.number().int().nonnegative(),
  resolvedCount: z.number().int().nonnegative(),
  matchStrength: z.enum(['none', 'limited', 'possible', 'strong', 'verified']).default('none'),
  verifiedCount: z.number().int().nonnegative().default(0),
  unverifiedCount: z.number().int().nonnegative().default(0),
  topCategory: z.string().max(40).nullable().optional(),
  categories: z.array(z.string().max(40)).max(12).default([]),
  items: z.array(QaContextItemSchema).max(10).default([]),
});
export type KnowledgeQaHistoricalContext = z.infer<typeof KnowledgeQaHistoricalContextSchema>;

export const KnowledgeQaResolutionContextSchema = QaContextSectionBaseSchema.extend({
  band: z.enum(['none', 'unverified', 'verified', 'possible', 'limited']),
  verifiedCount: z.number().int().nonnegative().default(0),
  unverifiedCount: z.number().int().nonnegative().default(0),
  items: z.array(QaContextItemSchema).max(8).default([]),
});
export type KnowledgeQaResolutionContext = z.infer<typeof KnowledgeQaResolutionContextSchema>;

export const KnowledgeQaMaintenanceContextSchema = QaContextSectionBaseSchema.extend({
  outcome: z.string().max(40).nullable(),
  suggestedAction: z.string().max(300).nullable(),
  evidenceLean: z.string().max(40).nullable().optional(),
  openQueueCount: z.number().int().nonnegative().default(0),
  assignedToMeCount: z.number().int().nonnegative().default(0),
  staleCount: z.number().int().nonnegative().default(0),
  draftHintCount: z.number().int().nonnegative().default(0),
  healingRecommendation: z.string().max(40).nullable().optional(),
  items: z.array(QaContextItemSchema).max(10).default([]),
});
export type KnowledgeQaMaintenanceContext = z.infer<typeof KnowledgeQaMaintenanceContextSchema>;

export const KnowledgeQaRegressionContextSchema = QaContextSectionBaseSchema.extend({
  suggestedFlowCount: z.number().int().nonnegative(),
  suggestedScenarioCount: z.number().int().nonnegative(),
  impactLevel: z.string().max(40).nullable().optional(),
  analysisStatus: z.string().max(40).nullable().optional(),
  /** Feature 4B — selected vs suite (compose); never invents safe-skip. */
  suiteFlowCount: z.number().int().nonnegative().optional(),
  notSelectedCount: z.number().int().nonnegative().optional(),
  selectionRatio: z.number().min(0).max(1).nullable().optional(),
  selectionMetricsStatus: z.enum(['available', 'limited', 'insufficient_evidence']).nullable().optional(),
  safeSkip: z.object({
    /** Feature 4D — structured; safe_to_skip never claimed without positive threshold. */
    status: z.enum(['available', 'limited', 'unavailable']),
    summary: z.string().max(300),
    overall: z
      .enum(['run_recommended', 'review_required', 'insufficient_evidence', 'safe_to_skip'])
      .nullable()
      .optional(),
    runRecommendedCount: z.number().int().nonnegative().optional(),
    reviewRequiredCount: z.number().int().nonnegative().optional(),
    insufficientEvidenceCount: z.number().int().nonnegative().optional(),
    safeToSkipCount: z.number().int().nonnegative().optional(),
  }),
  items: z.array(QaContextItemSchema).max(12).default([]),
});
export type KnowledgeQaRegressionContext = z.infer<typeof KnowledgeQaRegressionContextSchema>;

/** Feature 4C — coverage intelligence soft-join (counts + gaps; not a %). */
export const KnowledgeQaCoverageContextSchema = QaContextSectionBaseSchema.extend({
  coverageStatus: z.enum(['available', 'limited', 'insufficient_evidence']).nullable().optional(),
  projectRequirementTotal: z.number().int().nonnegative().default(0),
  projectRequirementWithScenario: z.number().int().nonnegative().default(0),
  projectRequirementUncovered: z.number().int().nonnegative().default(0),
  nearChangeKnownCount: z.number().int().nonnegative().default(0),
  coveredCount: z.number().int().nonnegative().default(0),
  partiallyCoveredCount: z.number().int().nonnegative().default(0),
  scenarioOnlyCount: z.number().int().nonnegative().default(0),
  notCoveredCount: z.number().int().nonnegative().default(0),
  unknownCount: z.number().int().nonnegative().default(0),
  impactedScenarioManualCount: z.number().int().nonnegative().default(0),
  items: z.array(QaContextItemSchema).max(12).default([]),
});
export type KnowledgeQaCoverageContext = z.infer<typeof KnowledgeQaCoverageContextSchema>;

export const KnowledgeQaReleaseContextSchema = QaContextSectionBaseSchema.extend({
  verdict: z.string().max(40).nullable().optional(),
  gateStatus: z.string().max(40).nullable().optional(),
  contractFailedRunCount: z.number().int().nonnegative().default(0),
  items: z.array(QaContextItemSchema).max(8).default([]),
});
export type KnowledgeQaReleaseContext = z.infer<typeof KnowledgeQaReleaseContextSchema>;

export const KnowledgeQaEnvironmentContextSchema = QaContextSectionBaseSchema.extend({
  /** Legacy: failure taxonomy counts from Change Impact — not deployment env. */
  classifications: z
    .array(
      z.object({
        category: z.string().max(40),
        count: z.number().int().nonnegative(),
      }),
    )
    .max(12)
    .default([]),
  /** Capability #12 — unified deployment/diagnostic bands (compose-on-read). */
  evidenceBand: EnvironmentEvidenceBandSchema.optional(),
  dimensions: z.array(EnvironmentDimensionEvidenceSchema).max(12).optional(),
});
export type KnowledgeQaEnvironmentContext = z.infer<typeof KnowledgeQaEnvironmentContextSchema>;

/** Wave 3C — explicit production/execution evidence honesty (not health). */
export const KnowledgeQaProductionEvidenceSchema = QaContextSectionBaseSchema.extend({
  band: z.enum(['available', 'limited', 'insufficient_evidence', 'unavailable']),
  windowDays: z.number().int().positive(),
  recentRunCount: z.number().int().nonnegative(),
  openFailureCount: z.number().int().nonnegative(),
  resolvedFailureCount: z.number().int().nonnegative(),
  claimsProductionHealthy: z.literal(false),
  claimsSafeSkip: z.literal(false),
});
export type KnowledgeQaProductionEvidence = z.infer<typeof KnowledgeQaProductionEvidenceSchema>;

export const KnowledgeQaGovernanceSchema = z.object({
  autoApply: z.literal(false),
  silentPublish: z.literal(false),
  recommendationsAreNotTruth: z.literal(true),
  advisoryOnly: z.literal(true),
});
export type KnowledgeQaGovernance = z.infer<typeof KnowledgeQaGovernanceSchema>;

/** Deterministic grounded answers — never LLM. */
export const KnowledgeQaAnswerSchema = z.object({
  question: z.string().max(200),
  answer: z.string().max(600),
  insufficientEvidence: z.boolean(),
});
export type KnowledgeQaAnswer = z.infer<typeof KnowledgeQaAnswerSchema>;

export const KnowledgeQaContextResponseSchema = z.object({
  projectId: z.string().uuid(),
  orgId: z.string().uuid(),
  composedAt: z.string(),
  attribution: z.literal('qa_context_compose'),
  projectContext: KnowledgeQaProjectContextSchema,
  changeImpact: KnowledgeQaChangeContextSchema,
  historicalFailures: KnowledgeQaHistoricalContextSchema,
  resolutions: KnowledgeQaResolutionContextSchema,
  maintenance: KnowledgeQaMaintenanceContextSchema,
  regression: KnowledgeQaRegressionContextSchema,
  coverage: KnowledgeQaCoverageContextSchema,
  releaseRisk: KnowledgeQaReleaseContextSchema,
  environment: KnowledgeQaEnvironmentContextSchema,
  productionEvidence: KnowledgeQaProductionEvidenceSchema,
  answers: z.array(KnowledgeQaAnswerSchema).max(12).default([]),
  governance: KnowledgeQaGovernanceSchema,
  limits: z.object({
    topN: z.number().int().positive(),
    authoringContextUnchanged: z.literal(true),
    zeroNewLlmCalls: z.literal(true),
  }),
});
export type KnowledgeQaContextResponse = z.infer<typeof KnowledgeQaContextResponseSchema>;
